import { useCallback } from 'react';
import { useAppSelector } from '@/store';

/**
 * Rechte des angemeldeten Benutzers.
 *
 * Ein Recht heißt AKTION:gegenstand — 'READ:user', 'CONFIGURE:tenant'. Genau
 * diese Zeichenketten stehen im Token und genau gegen sie prüft auch der
 * PermissionsGuard der Dienste. Die drei Verwaltungsseiten fragten nach
 * 'users:manage', 'roles:manage' und 'tenant:configure' — eine Schreibweise,
 * die es nirgends gibt. Der Vergleich ist exakt, also traf er nie zu: die
 * Benutzer-, Rollen- und Einstellungsseite waren für jeden gesperrt, auch für
 * ein Konto mit allen 75 Rechten.
 */
export function usePermissions() {
  const user = useAppSelector((state) => state.auth.user);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user) return false;
      return (user.permissions ?? []).includes(permission);
    },
    [user]
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]): boolean => {
      if (!user) return false;
      return permissions.some((p) => (user.permissions ?? []).includes(p));
    },
    [user]
  );

  const hasAllPermissions = useCallback(
    (permissions: string[]): boolean => {
      if (!user) return false;
      return permissions.every((p) => (user.permissions ?? []).includes(p));
    },
    [user]
  );

  const hasRole = useCallback(
    (role: string): boolean => {
      if (!user) return false;
      return (user.roles ?? []).includes(role);
    },
    [user]
  );

  const hasAnyRole = useCallback(
    (roles: string[]): boolean => {
      if (!user) return false;
      return roles.some((r) => (user.roles ?? []).includes(r));
    },
    [user]
  );

  // Keine Sonderstellung über Rollennamen: Die vergibt der Mandant selbst,
  // und SUPER_ADMIN/TENANT_ADMIN sind reserviert (GAP S-02). Die Felder
  // bleiben für bestehende Aufrufer, sind aber immer false.
  const isSuperAdmin = false;
  const isTenantAdmin = false;
  const isAuditDirector = user?.roles?.includes('Revisionsleitung') ?? false;

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    isSuperAdmin,
    isTenantAdmin,
    isAuditDirector,
  };
}
